/**
 * Employment helpers — part-time working days + derived tenure / FTE.
 */
export const WORKING_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
export const WORKING_DAY_LABELS = {
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
};
export const ALL_WORKING_DAYS = { mon: true, tue: true, wed: true, thu: true, fri: true };
/** Whole months of service from `startDate` to `today` (YYYY-MM-DD). The
 *  trailing partial month counts as a full month only if ≥15 days have elapsed
 *  in it (1–14 days → 0). */
export function monthsOfService(startDate, today) {
    const sy = Number(startDate.slice(0, 4));
    const sm = Number(startDate.slice(5, 7));
    const sd = Number(startDate.slice(8, 10));
    const ty = Number(today.slice(0, 4));
    const tm = Number(today.slice(5, 7));
    const td = Number(today.slice(8, 10));
    let months = (ty - sy) * 12 + (tm - sm);
    let dayDelta = td - sd;
    if (dayDelta < 0) {
        months -= 1;
        // Days in the month before `today` (the partial-month denominator).
        const prev = new Date(Date.UTC(ty, tm - 1, 0)).getUTCDate();
        dayDelta += prev;
    }
    if (dayDelta >= 15)
        months += 1;
    return Math.max(0, months);
}
/** Years of service as x.x (one decimal). */
export function yearsOfService(startDate, today) {
    if (!startDate)
        return null;
    return Math.round((monthsOfService(startDate, today) / 12) * 10) / 10;
}
/** Normalize + sort the weekly-hours history (ascending). When empty, synthesize
 *  a baseline from the legacy single `weeklyWorkingHours` (effective from the
 *  employment start date, else the epoch) so old data still resolves. */
export function resolveWeeklyHoursHistory(u) {
    const hist = (u.weeklyHoursHistory ?? []).filter((e) => e && typeof e.hours === 'number' && /^\d{4}-\d{2}-\d{2}$/.test(e.effectiveFrom));
    if (hist.length > 0)
        return [...hist].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
    if (u.weeklyWorkingHours != null) {
        // Synthesize a baseline from the legacy top-level fields, INCLUDING part-time
        // + working days, so existing part-timers get correct per-day targets +
        // vacation fraction without a timeline entry or migration.
        const base = { effectiveFrom: u.employmentStartDate || '1900-01-01', hours: u.weeklyWorkingHours };
        if (u.partTime && u.workingDays) {
            base.partTime = true;
            base.workingDays = u.workingDays;
        }
        return [base];
    }
    return [];
}
/** Weekly hours effective on `dateStr` = the latest entry with effectiveFrom ≤ date. */
export function effectiveWeeklyHours(u, dateStr) {
    let v = null;
    for (const e of resolveWeeklyHoursHistory(u)) {
        if (e.effectiveFrom <= dateStr)
            v = e.hours;
        else
            break;
    }
    return v;
}
/** Today's effective weekly hours (caller passes today as YYYY-MM-DD). */
export function currentWeeklyHours(u, today) {
    return effectiveWeeklyHours(u, today);
}
/** The working-time entry effective on `dateStr` = latest with effectiveFrom ≤ date. */
export function resolveWorkTimeEntry(u, dateStr) {
    let v = null;
    for (const e of resolveWeeklyHoursHistory(u)) {
        if (e.effectiveFrom <= dateStr)
            v = e;
        else
            break;
    }
    return v;
}
/** Mon–Fri key for a YYYY-MM-DD, or null on a weekend (UTC to avoid TZ drift). */
export function workingDayKeyForDate(dateStr) {
    const dow = new Date(`${dateStr}T00:00:00Z`).getUTCDay(); // 0=Sun..6=Sat
    if (dow === 0 || dow === 6)
        return null;
    return WORKING_DAY_KEYS[dow - 1] ?? null;
}
/** Count of worked weekdays in an entry (full-time / absent workingDays = 5). */
export function workingDayCount(entry) {
    if (!entry?.workingDays)
        return 5;
    return WORKING_DAY_KEYS.filter((k) => entry.workingDays[k]).length;
}
/** Fraction of a full week this arrangement works (worked weekdays ÷ 5). Full-time
 *  → 1.0. Used to pro-rate vacation entitlement (4 of 5 days → 0.8). */
export function workingFractionOfWeek(entry) {
    return workingDayCount(entry) / 5;
}
/** Is `key` a worked weekday in this arrangement? (absent workingDays = yes). */
export function isWorkedWeekday(entry, key) {
    if (!entry?.workingDays)
        return true;
    return !!entry.workingDays[key];
}
/** Hours targeted on a given weekday under this arrangement. Non-working day → 0.
 *  Uses the explicit per-day split when present, else an even split across the
 *  worked days. */
export function dayHoursForWeekday(entry, key) {
    if (!entry)
        return 0;
    if (!isWorkedWeekday(entry, key))
        return 0;
    const explicit = entry.dayHours?.[key];
    if (typeof explicit === 'number')
        return explicit;
    const n = workingDayCount(entry);
    return n > 0 ? entry.hours / n : 0;
}
/** Hours targeted on a specific date (0 on weekends + non-working weekdays). */
export function dayHoursForDate(entry, dateStr) {
    const key = workingDayKeyForDate(dateStr);
    if (!key)
        return 0;
    return dayHoursForWeekday(entry, key);
}
/** Is `dateStr` a worked day for this user (Mon–Fri worked-weekday, per the
 *  effective arrangement)? Weekends + non-working weekdays → false. */
export function isWorkedDate(u, dateStr) {
    const key = workingDayKeyForDate(dateStr);
    if (!key)
        return false;
    return isWorkedWeekday(resolveWorkTimeEntry(u, dateStr), key);
}
/** FTE for a calendar month (WHOLE-MONTH snapshot: hours effective on the 1st,
 *  or on the start date for a mid-month starter). Null when the person isn't
 *  employed that month or FTE can't be computed. */
export function monthlyFte(u, year, month0) {
    const mm = String(month0 + 1).padStart(2, '0');
    const first = `${year}-${mm}-01`;
    const lastDay = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
    const last = `${year}-${mm}-${String(lastDay).padStart(2, '0')}`;
    if (u.employmentStartDate && u.employmentStartDate > last)
        return null; // not started
    if (u.employmentEndDate && u.employmentEndDate < first)
        return null; // already left
    const ref = u.employmentStartDate && u.employmentStartDate > first ? u.employmentStartDate : first;
    return fteValue(effectiveWeeklyHours(u, ref), u.fullTimeWeeklyHours, u.partTime);
}
/** Resolve the configured FTE groups (or []). */
export function resolveFteGroups(config) {
    return (config?.fteEntityGroups ?? []).filter((g) => g && g.name && Array.isArray(g.entities));
}
/** Does a user's employment entity belong to this group? (case-insensitive,
 *  trimmed; '' matches an unset entity). */
export function userInFteGroup(employmentEntity, group) {
    const ent = (employmentEntity ?? '').trim().toLowerCase();
    return group.entities.some((e) => e.trim().toLowerCase() === ent);
}
/** FTE = weekly hours ÷ full-time hours. Full-time (no separate full-time hours)
 *  → 1.0. Returns null when hours are missing. Rounded to 2 decimals. */
export function fteValue(weeklyHours, fullTimeHours, partTime) {
    if (!weeklyHours || weeklyHours <= 0)
        return null;
    const ft = partTime ? fullTimeHours : (fullTimeHours || weeklyHours);
    if (!ft || ft <= 0)
        return null;
    return Math.round((weeklyHours / ft) * 100) / 100;
}
//# sourceMappingURL=employment.js.map